import { useCallback } from 'react';
import { getAlbumService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';
import type {
  Album,
  AlbumsQueryParams,
  CreateAlbumRequest,
  UpdateAlbumRequest,
} from '../types/api/album';

export function useAlbums() {
  const albumService = getAlbumService();

  const fetchAlbums: FetchDataFn<Album> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Album>> => {
      try {
        const apiParams: AlbumsQueryParams = {
          page: params.page,
          size: params.pageSize,
          keyword: params.search,
        };

        const response = await albumService.getAlbums(apiParams);

        return {
          data: response.items,
          pagination: {
            page: response.page,
            pageSize: response.size,
            total: response.totalItems,
            totalPages: response.totalPages,
            hasNext: response.hasNext,
            hasPrev: response.hasPrevious,
          },
        };
      } catch (error) {
        console.error('Failed to fetch albums:', error);
        throw new Error('Failed to load albums. Please try again.');
      }
    },
    [albumService]
  );

  return {
    fetchAlbums,
  };
}

export function useAlbumActions() {
  const albumService = getAlbumService();

  const createAlbum = useCallback(
    async (albumData: CreateAlbumRequest, coverImageFile?: File, imageFile?: File): Promise<Album> => {
      try {
        return await albumService.createAlbumMultipart(albumData, coverImageFile, imageFile);
      } catch (error) {
        console.error('Failed to create album:', error);
        throw error;
      }
    },
    [albumService]
  );

  const updateAlbum = useCallback(
    async (
      albumId: string,
      albumData: UpdateAlbumRequest,
      coverImageFile?: File,
      imageFile?: File
    ): Promise<Album> => {
      try {
        return await albumService.updateAlbumMultipart(albumId, albumData, coverImageFile, imageFile);
      } catch (error) {
        console.error('Failed to update album:', error);
        throw error;
      }
    },
    [albumService]
  );

  const getAlbum = useCallback(
    async (albumId: string): Promise<Album> => {
      try {
        return await albumService.getAlbumById(albumId);
      } catch (error) {
        console.error('Failed to fetch album:', error);
        throw error;
      }
    },
    [albumService]
  );

  const deleteAlbum = useCallback(
    async (albumId: string): Promise<void> => {
      try {
        await albumService.deleteAlbum(albumId);
      } catch (error) {
        console.error('Failed to delete album:', error);
        throw error;
      }
    },
    [albumService]
  );

  return {
    createAlbum,
    updateAlbum,
    getAlbum,
    deleteAlbum,
  };
}
